// Feedback.js — shared toast + confirm-dialog primitives for admin screens.
// Extracted from the Toast/RestoreModal pattern already used in
// DeletedStudents.js, so replacing native alert()/window.confirm() calls
// (see ECOSYSTEM_POLISH_AUDIT.md MS-2/MS-3) reuses one implementation
// instead of each screen rolling its own.
import React, { useState, useCallback, useRef } from 'react';

export const Toast = ({ msg, type = 'success' }) => (
  <div style={{
    position: 'fixed', top: 24, right: 24, zIndex: 4000,
    background: type === 'success' ? '#43e97b' : '#f5576c',
    color: '#000', fontWeight: 700, borderRadius: 10,
    padding: '12px 22px', fontSize: 14, maxWidth: 360,
    boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
    animation: 'fadeIn 0.2s ease',
  }}>
    {msg}
  </div>
);

export function useToast() {
  const [toast, setToast] = useState(null);
  const timer = useRef(null);

  const showToast = useCallback((msg, type = 'success') => {
    if (timer.current) clearTimeout(timer.current);
    setToast({ msg, type });
    timer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  return { toast, showToast };
}

export const ConfirmModal = ({ title, message, confirmLabel = 'Delete', danger = true, loading, onConfirm, onCancel }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 4100,
    background: 'rgba(0,0,0,0.72)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <div style={{
      background: '#13131f', border: '1px solid #2a2a3d',
      borderRadius: 14, padding: '32px', width: 400, maxWidth: '92vw',
      textAlign: 'center',
    }}>
      <h3 style={{ margin: '0 0 10px', color: '#f9fafb', fontSize: 17 }}>{title}</h3>
      <p style={{ margin: '0 0 24px', color: '#9ca3af', fontSize: 14, lineHeight: 1.5 }}>{message}</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button
          onClick={onCancel}
          disabled={loading}
          style={{
            padding: '9px 20px', borderRadius: 8, border: '1px solid #2a2a3d',
            background: 'transparent', color: '#9ca3af', fontSize: 13,
            fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          style={{
            padding: '9px 20px', borderRadius: 8, border: 'none',
            background: danger ? '#f5576c' : '#43e97b', color: '#000',
            fontWeight: 700, fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Please wait…' : confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// Queues a single pending confirm action. requestConfirm(opts, action) opens
// the dialog; confirm() runs the action and only clears state once it
// settles, so callers get a real loading state instead of an instant
// blocking window.confirm().
export function useConfirm() {
  const [state, setState] = useState(null);

  const requestConfirm = useCallback((opts, action) => {
    setState({ ...opts, action, loading: false });
  }, []);

  const cancel = useCallback(() => setState(null), []);

  const confirm = useCallback(async () => {
    setState(s => (s ? { ...s, loading: true } : s));
    try {
      await state?.action();
    } finally {
      setState(null);
    }
  }, [state]);

  return { confirmState: state, requestConfirm, confirm, cancel };
}
